import { useState, useEffect, useCallback } from 'react';
import type { MonthlyShiftSlotSummary } from '../../../../domain/monthlyShiftSlot';
import type { MonthlyShiftSlotSummaryRepository } from '../../../../application/monthlyShiftSlot/ports/MonthlyShiftSlotSummaryRepository';
import {
  getMonthlyShiftSlotSummaryList,
  deleteMonthlyShiftSlot
} from '../../../../application/monthlyShiftSlot';

/**
 * 月次シフト枠管理フックの内部状態
 *
 * @property {MonthlyShiftSlotSummary[]} data - サマリーデータ配列
 * @property {object|null} meta - ページネーションメタ情報
 * @property {boolean} isLoading - ローディング状態
 * @property {string|null} error - エラーメッセージ
 * @property {boolean} deleteDialogOpen - 削除確認ダイアログの開閉状態
 * @property {MonthlyShiftSlotSummary|null} selectedSummary - 選択中のサマリー（削除対象）
 */
export interface MonthlyShiftSlotManagerState {
  data: MonthlyShiftSlotSummary[];
  meta: {
    currentPage: number;
    lastPage: number;
    perPage: number;
    total: number;
  } | null;
  isLoading: boolean;
  error: string | null;
  deleteDialogOpen: boolean;
  selectedSummary: MonthlyShiftSlotSummary | null;
}

/**
 * useMonthlyShiftSlotManager フックの戻り値型
 *
 * @extends MonthlyShiftSlotManagerState
 * @property {function} fetchSummaries - サマリー一覧を取得する
 * @property {function} handlePageChange - ページ変更ハンドラー
 * @property {function} showDeleteDialog - 削除確認ダイアログを表示する
 * @property {function} closeDeleteDialog - 削除確認ダイアログを閉じる
 * @property {function} deleteSummary - サマリーを削除する
 * @property {function} clearError - エラーをクリアする
 */
export interface UseMonthlyShiftSlotManagerReturn extends MonthlyShiftSlotManagerState {
  fetchSummaries: (page?: number) => Promise<void>;
  handlePageChange: (page: number) => void;
  showDeleteDialog: (summary: MonthlyShiftSlotSummary) => void;
  closeDeleteDialog: () => void;
  deleteSummary: (id: string) => Promise<boolean>;
  clearError: () => void;
}

/**
 * 月次シフト枠管理用カスタムフック
 *
 * @description
 * 月次シフト枠管理ページの状態管理とビジネスロジックを提供する。
 * 一覧取得、ページネーション、削除確認ダイアログ、削除処理を統合管理する。
 *
 * @param {MonthlyShiftSlotSummaryRepository} repository - DIされるRepository実装
 * @returns {UseMonthlyShiftSlotManagerReturn} 状態とアクション関数
 *
 * @example
 * const {
 *   data,
 *   isLoading,
 *   error,
 *   fetchSummaries,
 *   deleteSummary
 * } = useMonthlyShiftSlotManager(repository);
 */
export const useMonthlyShiftSlotManager = (
  repository: MonthlyShiftSlotSummaryRepository
): UseMonthlyShiftSlotManagerReturn => {
  const [state, setState] = useState<MonthlyShiftSlotManagerState>({
    data: [],
    meta: null,
    isLoading: false,
    error: null,
    deleteDialogOpen: false,
    selectedSummary: null
  });

  const fetchSummaries = useCallback(async (page?: number) => {
    setState(prev => ({ ...prev, isLoading: true, error: null }));

    try {
      const { result } = await getMonthlyShiftSlotSummaryList(repository, {
        page: page ?? state.meta?.currentPage ?? 1,
        pageSize: 50
      });

      setState(prev => ({
        ...prev,
        data: result.data,
        meta: result.meta,
        isLoading: false
      }));
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'データ取得に失敗しました';
      setState(prev => ({
        ...prev,
        isLoading: false,
        error: errorMessage
      }));
    }
  }, [repository, state.meta?.currentPage]);

  useEffect(() => {
    fetchSummaries();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handlePageChange = useCallback((page: number) => {
    fetchSummaries(page);
  }, [fetchSummaries]);

  const showDeleteDialog = useCallback((summary: MonthlyShiftSlotSummary) => {
    setState(prev => ({
      ...prev,
      deleteDialogOpen: true,
      selectedSummary: summary
    }));
  }, []);

  const closeDeleteDialog = useCallback(() => {
    setState(prev => ({
      ...prev,
      deleteDialogOpen: false,
      selectedSummary: null
    }));
  }, []);

  const deleteSummary = useCallback(async (id: string): Promise<boolean> => {
    setState(prev => ({ ...prev, isLoading: true, error: null }));

    const result = await deleteMonthlyShiftSlot(repository, { id });

    if (!result.success) {
      setState(prev => ({
        ...prev,
        isLoading: false,
        error: result.errors?.[0] || '削除に失敗しました'
      }));
      return false;
    }

    await fetchSummaries();
    closeDeleteDialog();
    setState(prev => ({ ...prev, isLoading: false }));

    return true;
  }, [repository, fetchSummaries, closeDeleteDialog]);

  const clearError = useCallback(() => {
    setState(prev => ({ ...prev, error: null }));
  }, []);

  return {
    ...state,
    fetchSummaries,
    handlePageChange,
    showDeleteDialog,
    closeDeleteDialog,
    deleteSummary,
    clearError
  };
};
